/**
* IBM Confidential
* Licensed Materials - Property of IBM
* (C) Copyright IBM Corp. 2020, 2024 All Rights Reserved.
* US Government Users Restricted Rights - Use, duplication or
* disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
* */

import { objectSpread2 as _objectSpread2, extends as _extends } from '../../../_virtual/_rollupPluginBabelHelpers.js';
import React from 'react';
import { Tile, Button } from '@carbon/react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import useThemePreference from '../../../Utilities/useThemePreference/useThemePreference.js';
import Error from '../../../icons/Error.js';
import ErrorBlue from '../../../icons/ErrorBlue.js';
import ErrorGray from '../../../icons/ErrorGray.js';
import SupportBlue from '../../../icons/SupportBlue.js';
import SupportGray from '../../../icons/SupportGray.js';
import UsageBlue from '../../../icons/UsageBlue.js';
import UsageGray from '../../../icons/UsageGray.js';
import Empty from '../../../icons/Empty.js';
import Hierarchy from '../../../icons/Hierarchy.js';
import Money from '../../../icons/Money.js';
import NoResults from '../../../icons/NoResults.js';
import Notification from '../../../icons/Notification.js';
import Tag from '../../../icons/Tag.js';
import Unauthorized from '../../../icons/Unauthorized.js';
import ErrorDark from '../../../icons/ErrorDark.js';
import ErrorDarkBlue from '../../../icons/ErrorDarkBlue.js';
import ErrorDarkGray from '../../../icons/ErrorDarkGray.js';
import SupportDarkBlue from '../../../icons/SupportDarkBlue.js';
import SupportDarkGray from '../../../icons/SupportDarkGray.js';
import UsageDarkBlue from '../../../icons/UsageDarkBlue.js';
import UsageDarkGray from '../../../icons/UsageDarkGray.js';
import EmptyDark from '../../../icons/EmptyDark.js';
import NoResultsDark from '../../../icons/NoResultsDark.js';
import NotificationDark from '../../../icons/NotificationDark.js';
import TagDark from '../../../icons/TagDark.js';
import UnauthorizedDark from '../../../icons/UnauthorizedDark.js';

var fallbackIcon = {
  name: 'ERROR',
  color: 'GRAY'
};
var iconMap = {
  themeLight: {
    ERROR: {
      BLUE: Error,
      GRAY: Error
    },
    ERROR2: {
      BLUE: ErrorBlue,
      GRAY: ErrorGray
    },
    SUPPORT: {
      BLUE: SupportBlue,
      GRAY: SupportGray
    },
    USAGE: {
      BLUE: UsageBlue,
      GRAY: UsageGray
    },
    EMPTY: {
      BLUE: Empty,
      GRAY: Empty
    },
    HIERARCHY: {
      BLUE: Hierarchy,
      GRAY: Hierarchy
    },
    MONEY: {
      BLUE: Money,
      GRAY: Money
    },
    NORESULTS: {
      BLUE: NoResults,
      GRAY: NoResults
    },
    NOTIFICATION: {
      BLUE: Notification,
      GRAY: Notification
    },
    TAG: {
      BLUE: Tag,
      GRAY: Tag
    },
    UNAUTHORIZED: {
      BLUE: Unauthorized,
      GRAY: Unauthorized
    }
  },
  themeDark: {
    ERROR: {
      BLUE: ErrorDark,
      GRAY: ErrorDark
    },
    ERROR2: {
      BLUE: ErrorDarkBlue,
      GRAY: ErrorDarkGray
    },
    SUPPORT: {
      BLUE: SupportDarkBlue,
      GRAY: SupportDarkGray
    },
    USAGE: {
      BLUE: UsageDarkBlue,
      GRAY: UsageDarkGray
    },
    EMPTY: {
      BLUE: EmptyDark,
      GRAY: EmptyDark
    },
    HIERARCHY: {
      BLUE: Hierarchy,
      GRAY: Hierarchy
    },
    MONEY: {
      BLUE: Money,
      GRAY: Money
    },
    NORESULTS: {
      BLUE: NoResultsDark,
      GRAY: NoResultsDark
    },
    NOTIFICATION: {
      BLUE: NotificationDark,
      GRAY: NotificationDark
    },
    TAG: {
      BLUE: TagDark,
      GRAY: TagDark
    },
    UNAUTHORIZED: {
      BLUE: UnauthorizedDark,
      GRAY: UnauthorizedDark
    }
  }
};
var Message = function Message(_ref) {
  var className = _ref.className,
    button = _ref.button,
    _ref$icon = _ref.icon,
    icon = _ref$icon === void 0 ? fallbackIcon : _ref$icon,
    _ref$tileWrapped = _ref.tileWrapped,
    tileWrapped = _ref$tileWrapped === void 0 ? true : _ref$tileWrapped,
    text = _ref.text,
    caption = _ref.caption,
    children = _ref.children,
    _ref$size = _ref.size,
    size = _ref$size === void 0 ? 'md' : _ref$size;
  var classes = classnames(className, 'cpc--message', "cpc--message--".concat(size));
  var theme = useThemePreference();
  var Icon = null;
  var iconPropIsReact = false;
  if (icon !== null && icon !== void 0 && icon.name || typeof icon === 'string') {
    Icon = iconMap[theme][(icon === null || icon === void 0 ? void 0 : icon.name) || icon || 'ERROR'][(icon === null || icon === void 0 ? void 0 : icon.color) || 'GRAY'];
  } else if ( /*#__PURE__*/React.isValidElement(icon)) {
    iconPropIsReact = true;
    Icon = icon;
  }
  var renderContent = function renderContent() {
    return /*#__PURE__*/React.createElement(React.Fragment, null, iconPropIsReact ? Icon : /*#__PURE__*/React.createElement("div", {
      className: "cpc--message__icon"
    }, /*#__PURE__*/React.createElement(Icon, {
      id: String(Math.random())
    })), /*#__PURE__*/React.createElement("div", {
      className: "cpc--message__content"
    }, /*#__PURE__*/React.createElement("span", {
      className: "cpc--message__text"
    }, text), caption && /*#__PURE__*/React.createElement("span", {
      className: "cpc--message__caption"
    }, caption), button && /*#__PURE__*/React.createElement("div", {
      className: "cpc--message__button-container"
    }, /*#__PURE__*/React.createElement(Button, _extends({
      kind: "tertiary",
      size: "sm"
    }, button), button.text)), children && /*#__PURE__*/React.createElement("div", {
      className: "cpc--message__children-container"
    }, children)));
  };
  if (!tileWrapped) {
    return /*#__PURE__*/React.createElement("div", {
      className: classes
    }, renderContent());
  }
  return /*#__PURE__*/React.createElement("div", {
    className: classes
  }, /*#__PURE__*/React.createElement(Tile, null, renderContent()));
};
var ALLOWED_ICONS = ['ERROR', 'ERROR2', 'SUPPORT', 'USAGE', 'EMPTY', 'HIERARCHY', 'MONEY', 'NORESULTS', 'NOTIFICATION', 'TAG', 'UNAUTHORIZED'];
Message.propTypes = {
  /**
   * Custom class name
   */
  className: PropTypes.string,
  /**
   * Identifier for icon to be used
   */
  icon: PropTypes.oneOfType([PropTypes.shape({
    color: PropTypes.oneOf(['BLUE', 'GRAY']),
    name: PropTypes.oneOf(ALLOWED_ICONS)
  }), PropTypes.oneOf(ALLOWED_ICONS), PropTypes.node]),
  /**
   * Boolean to determine whether component should be wrapped in a tile
   */
  tileWrapped: PropTypes.bool,
  /**
   * Title text
   */
  text: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  /**
   * Message caption beneath text
   */
  caption: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  /**
   * You can provide extra children nodes if needed
   */
  children: PropTypes.node,
  /**
   * Size of component
   */
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  /**
   * Button props
   */
  button: PropTypes.shape(_objectSpread2(_objectSpread2({}, Button.defaultProps), {}, {
    text: PropTypes.string
  }))
};
var Message$1 = Message;

export { Message$1 as default };
